import {
  Image as CrystallizeImage,
  ImageContentFragment,
  ImageFragment,
  ParagraphContent,
  ProductVariant,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import Image from 'next/image';
import React from 'react';

interface OptimizedImageProps {
  imageComponent?: ProductVariant | ImageContentFragment | ParagraphContent | null;
  image?: ImageFragment | null;
  // Overrides the alt text stored on the image, for callers that compose it
  // from surrounding data (e.g. product title + variant color + view).
  alt?: string;
  width?: number;
  height?: number;
  fill?: boolean;
  priority?: boolean;
  className?: string;
  sizes: string;
  dataTestId?: string;
  onLoad?: () => void;
  onError?: () => void;
}

function findLargestWebpUrl(image: ImageFragment | CrystallizeImage) {
  const { variants } = image;

  if (image.url.endsWith('.svg')) {
    return image.url;
  }

  if (variants && variants.length > 0) {
    let largestUrl = '';
    let largestSize = 0;
    // get item which variant.url ends with .webp and with the largest variant.width
    variants.forEach((variant) => {
      if (variant.url.endsWith('.webp') && variant.width > largestSize) {
        largestSize = variant.width;
        largestUrl = variant.url;
      }
    });

    return largestUrl;
  }

  return image.url;
}

// forwardRef so callers grab the underlying <img> via the standard `ref` prop (Next forwards it to
// the real DOM node) — e.g. to read `img.complete` for cached-image handling.
const OptimizedImage = React.forwardRef<HTMLImageElement, Readonly<OptimizedImageProps>>(
  (props, ref) => {
    const {
      imageComponent,
      image,
      alt,
      width,
      height,
      fill,
      priority,
      className,
      sizes,
      dataTestId,
      onLoad,
      onError,
    } = props;

    const extractedImage = imageComponent?.images?.[0] ?? image;

    if (!extractedImage) {
      return null;
    }

    const { altText } = extractedImage;
    const focalPointX = (extractedImage.focalPoint?.x ?? 0.5) * 100;
    const focalPointY = (extractedImage.focalPoint?.y ?? 0.5) * 100;

    const webpUrl = findLargestWebpUrl(extractedImage);

    /*
    NOTICE
    The upcoming usage of the Next Image component
    uses some custom image loader behind the scenes
    placed in the /src/utils/image-loader.ts file.
    This comment exists to get rid of potential doubt
    in case you want to identify how/why the image
    path is at it is.
    */
    return (
      <Image
        src={webpUrl}
        alt={alt ?? altText ?? ''}
        width={width}
        height={height}
        fill={fill}
        priority={priority}
        className={className}
        sizes={sizes}
        data-testid={dataTestId}
        onLoad={onLoad}
        onError={onError}
        ref={ref}
        style={{
          objectPosition: `${focalPointX}% ${focalPointY}%`,
        }}
      />
    );
  },
);

OptimizedImage.displayName = 'OptimizedImage';

export default OptimizedImage;
